/**
 * Converte uma imagem remota (inclusive WebP) em um data URL JPEG.
 *
 * Motivação: @react-pdf/renderer NÃO suporta WebP — apenas JPEG/PNG. Como o
 * bucket `service-order-images` guarda WebP, a signed URL precisa ser convertida
 * ANTES de ir para o <Image> do PDF.
 *
 * Pipeline: fetch(url) → blob → Image via objectURL → canvas → toDataURL('image/jpeg').
 * O objectURL é sempre revogado. Em caso de erro a Promise é rejeitada e o
 * caller decide o que fazer (ex.: pular a foto).
 *
 * @param url    URL da imagem (ex.: signed URL do Supabase Storage).
 * @param quality Qualidade JPEG (0–1). Default 0.85.
 * @returns data URL no formato `data:image/jpeg;base64,...`
 */
export async function imageToJpegDataUrl(url: string, quality = 0.85): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Falha ao baixar imagem (${response.status})`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas 2D indisponível'));
            return;
          }
          // Fundo branco: JPEG não tem transparência.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Imagem inválida'));
      img.src = objectUrl;
    });
    return dataUrl;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
